/**
 * useAutoRefresh Hook
 * Manages auto-refresh intervals for dashboard components
 * Extracted from App.js to eliminate duplication
 */

import { useState, useEffect, useRef } from 'react';

/**
 * Auto-refresh hook with configurable intervals and enable/disable toggle
 *
 * @param {Function} refreshCallback - Function to call on each refresh
 * @param {number} interval - Refresh interval in milliseconds
 * @param {boolean} initialEnabled - Initial enabled state (default: true)
 * @param {Array} deps - Additional dependencies (unused, kept for API compatibility)
 * @returns {Object} { enabled, setEnabled, lastRefreshTime }
 */
const useAutoRefresh = (refreshCallback, interval, initialEnabled = true, deps = []) => {
    const [enabled, setEnabled] = useState(initialEnabled);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);
    const timerRef = useRef(null);
    const callbackRef = useRef(refreshCallback);
    const hasRunInitial = useRef(false);

    // Keep callback ref up to date without triggering effect
    useEffect(() => {
        callbackRef.current = refreshCallback;
    }, [refreshCallback]);

    useEffect(() => {
        if (!enabled) {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            hasRunInitial.current = false;
            return;
        }

        const runCallback = async () => {
            try {
                await callbackRef.current();
                setLastRefreshTime(new Date());
            } catch (error) {
                console.error('[AutoRefresh] Refresh failed:', error);
            }
        };

        // Only run immediately once when first enabled
        if (!hasRunInitial.current) {
            hasRunInitial.current = true;
            runCallback();
        }

        // Set up interval
        timerRef.current = setInterval(runCallback, interval);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [enabled, interval]);

    return {
        enabled,
        setEnabled,
        lastRefreshTime
    };
};

export default useAutoRefresh;
